/**
 * Immunizations service — FHIR Immunization-backed vaccination record.
 *
 * CVX codes are the default (CDC vaccine-administered code set). The UI
 * picker uses the existing NLM Clinical Tables typeahead (see
 * allergyService.ts) for the long-tail CVX lookup; common vaccines are
 * exported below as shortcuts.
 */

import { api } from './api';

export type ImmunizationStatus = 'completed' | 'entered-in-error' | 'not-done';

export interface PatientImmunization {
  id: string;
  patient_id: string;
  clinic_id: string;
  encounter_id?: string | null;
  performer_practitioner_id?: string | null;
  vaccine_code_system: string;
  vaccine_code: string;
  vaccine_display: string;
  status: ImmunizationStatus;
  status_reason?: string | null;
  occurrence_date: string;
  recorded_at: string;
  lot_number?: string | null;
  expiration_date?: string | null;
  site?: string | null;
  route?: string | null;
  dose_quantity_value?: number | null;
  dose_quantity_unit?: string | null;
  dose_number?: number | null;
  series_doses?: number | null;
  note?: string | null;
  performer?: { id: string; first_name: string; last_name: string } | null;
}

export interface RecordImmunizationPayload {
  vaccineCode: string;
  vaccineDisplay: string;
  vaccineCodeSystem?: string;
  status?: ImmunizationStatus;
  statusReason?: string;
  occurrenceDate: string;
  lotNumber?: string;
  expirationDate?: string;
  site?: string;
  route?: string;
  doseQuantityValue?: number;
  doseQuantityUnit?: string;
  doseNumber?: number;
  seriesDoses?: number;
  performerPractitionerId?: string;
  note?: string;
  // Encounter linkage — required by requireClinicalContext middleware.
  encounterId?: string;
  off_encounter_reason?:
    | 'refill_request'
    | 'chart_correction'
    | 'letter'
    | 'external_import'
    | 'pre_visit_task'
    | 'direct_rx';
}

export const CommonVaccines = {
  COVID_PFIZER: { code: '208', display: 'COVID-19, mRNA, LNP-S, PF, 30 mcg/0.3 mL dose (Pfizer)' },
  COVID_MODERNA: { code: '207', display: 'COVID-19, mRNA, LNP-S, PF, 100 mcg/0.5 mL dose (Moderna)' },
  INFLUENZA_QUAD: { code: '150', display: 'Influenza, injectable, quadrivalent' },
  TDAP: { code: '115', display: 'Tdap (tetanus, diphtheria, pertussis)' },
  MMR: { code: '03', display: 'MMR (measles, mumps, rubella)' },
  HPV_9: { code: '165', display: 'HPV9 (Gardasil 9)' },
  SHINGLES_RZV: { code: '187', display: 'Shingrix (zoster recombinant)' },
  PNEUMOCOCCAL_PCV13: { code: '133', display: 'Pneumococcal conjugate PCV13' },
  HEPATITIS_B: { code: '43', display: 'Hepatitis B, adult' },
} as const;

export const immunizationsService = {
  async getPatientImmunizations(
    clinicId: string,
    patientId: string,
    options?: { status?: ImmunizationStatus },
  ): Promise<PatientImmunization[]> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    const qs = params.toString();
    const response = await api.get(
      `/emr/${clinicId}/patients/${patientId}/immunizations${qs ? `?${qs}` : ''}`,
    );
    return response.data || [];
  },

  async createPatientImmunization(
    clinicId: string,
    patientId: string,
    payload: RecordImmunizationPayload,
  ): Promise<PatientImmunization> {
    const body: RecordImmunizationPayload = {
      ...payload,
      off_encounter_reason:
        payload.off_encounter_reason
        || (payload.encounterId ? undefined : 'chart_correction'),
    };
    const response = await api.post(
      `/emr/${clinicId}/patients/${patientId}/immunizations`,
      body,
    );
    return response.data || response;
  },

  async createEncounterImmunization(
    clinicId: string,
    encounterId: string,
    payload: Omit<RecordImmunizationPayload, 'encounterId' | 'off_encounter_reason'>,
  ): Promise<PatientImmunization> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/immunizations`,
      payload,
    );
    return response.data || response;
  },

  async updateImmunization(
    clinicId: string,
    immunizationId: string,
    updates: Partial<{
      status: ImmunizationStatus;
      statusReason: string;
      lotNumber: string;
      expirationDate: string;
      site: string;
      route: string;
      doseQuantityValue: number;
      doseQuantityUnit: string;
      doseNumber: number;
      seriesDoses: number;
      note: string;
    }>,
  ): Promise<PatientImmunization> {
    const response = await api.put(
      `/emr/${clinicId}/immunizations/${immunizationId}`,
      updates,
    );
    return response.data || response;
  },
};

export default immunizationsService;
